/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package utils

import i18n.StringKey
import i18n.tr

/**
 * Shared utilities for formatting and handling Plaid transaction categories
 * across all platforms (iOS, Android, Web).
 */
object CategoryUtils {
    /** Curated 30-color palette for both categories and contexts. Works on dark and light themes. */
    val colorPalette = listOf(
        "#FF6B6B", "#FF8E72", "#F59E0B", "#FBBF24", "#FCD34D", "#A3E635",
        "#10B981", "#14B8A6", "#06B6D4", "#0EA5E9", "#3B82F6", "#6366F1",
        "#8B5CF6", "#A855F7", "#D946EF", "#EC4899", "#F43F5E", "#FB7185",
        "#78716C", "#A8A29E", "#94A3B8", "#64748B", "#475569", "#334155",
        "#EF4444", "#F97316", "#84CC16", "#22C55E", "#2DD4BF", "#818CF8"
    )

    /**
     * Formats Plaid's category format (UPPER_SNAKE_CASE) to display format (Title Case)
     * Example: "FOOD_AND_DRINK" -> "Food and Drink"
     */
    fun formatCategoryName(category: String): String =
        category
            .split("_")
            .joinToString(" ") { it.lowercase().replaceFirstChar { c -> c.uppercase() } }
            .replace(" And ", " and ")

    /**
     * Creates a two-level category display string from Plaid's hierarchical categories.
     * Example: formatTwoLevelCategory("FOOD_AND_DRINK", "FOOD_AND_DRINK_VENDING_MACHINES")
     * Returns: "Food and Drink → Vending Machines"
     */
    fun formatTwoLevelCategory(
        primary: String,
        detailed: String,
    ): String {
        val formattedPrimary = formatCategoryName(primary)

        // Extract subcategory by removing the primary prefix
        val subcategory = detailed.removePrefix("${primary}_")
        val formattedSubcategory =
            if (subcategory != detailed) {
                // Successfully removed prefix, format the subcategory part
                formatCategoryName(subcategory)
            } else {
                // No prefix match, format the whole detailed string
                formatCategoryName(detailed)
            }

        // Guard: if both levels format to the same string, show only one level
        if (formattedPrimary == formattedSubcategory) return formattedPrimary
        return "$formattedPrimary → $formattedSubcategory"
    }

    /**
     * Locale-aware map of Plaid primary category codes to StringKeys.
     */
    private val plaidCategoryKeys = mapOf(
        "INCOME" to StringKey.CATEGORY_INCOME,
        "TRANSFER_IN" to StringKey.CATEGORY_TRANSFER_IN,
        "TRANSFER_OUT" to StringKey.CATEGORY_TRANSFER_OUT,
        "LOAN_PAYMENTS" to StringKey.CATEGORY_LOAN_PAYMENTS,
        "BANK_FEES" to StringKey.CATEGORY_BANK_FEES,
        "ENTERTAINMENT" to StringKey.CATEGORY_ENTERTAINMENT,
        "FOOD_AND_DRINK" to StringKey.CATEGORY_FOOD_AND_DRINK,
        "GENERAL_MERCHANDISE" to StringKey.CATEGORY_GENERAL_MERCHANDISE,
        "HOME_IMPROVEMENT" to StringKey.CATEGORY_HOME_IMPROVEMENT,
        "MEDICAL" to StringKey.CATEGORY_MEDICAL,
        "PERSONAL_CARE" to StringKey.CATEGORY_PERSONAL_CARE,
        "GENERAL_SERVICES" to StringKey.CATEGORY_GENERAL_SERVICES,
        "GOVERNMENT_AND_NON_PROFIT" to StringKey.CATEGORY_GOVERNMENT_AND_NON_PROFIT,
        "TRANSPORTATION" to StringKey.CATEGORY_TRANSPORTATION,
        "TRAVEL" to StringKey.CATEGORY_TRAVEL,
        "RENT_AND_UTILITIES" to StringKey.CATEGORY_RENT_AND_UTILITIES,
        "FAMILY" to StringKey.CATEGORY_FAMILY,
        "OTHER" to StringKey.CATEGORY_OTHER,
        "MISCELLANEOUS" to StringKey.CATEGORY_MISCELLANEOUS,

        // Sub-categories: Income
        "DIVIDENDS" to StringKey.CATEGORY_INCOME_DIVIDENDS,
        "INTEREST_EARNED" to StringKey.CATEGORY_INCOME_INTEREST_EARNED,
        "RETIREMENT_PENSION" to StringKey.CATEGORY_INCOME_RETIREMENT_PENSION,
        "TAX_REFUND" to StringKey.CATEGORY_INCOME_TAX_REFUND,
        "UNEMPLOYMENT" to StringKey.CATEGORY_INCOME_UNEMPLOYMENT,
        "WAGES" to StringKey.CATEGORY_INCOME_WAGES,
        "OTHER_INCOME" to StringKey.CATEGORY_INCOME_OTHER,

        // Sub-categories: Transfer In
        "CASH_ADVANCES_AND_LOANS" to StringKey.CATEGORY_TRANSFER_IN_CASH_ADVANCES,
        "DEPOSIT" to StringKey.CATEGORY_TRANSFER_IN_DEPOSIT,
        "INVESTMENT_AND_RETIREMENT_FUNDS" to StringKey.CATEGORY_TRANSFER_IN_INVESTMENT,
        "SAVINGS" to StringKey.CATEGORY_TRANSFER_IN_SAVINGS,
        "ACCOUNT_TRANSFER" to StringKey.CATEGORY_TRANSFER_IN_ACCOUNT,
        "OTHER_TRANSFER_IN" to StringKey.CATEGORY_TRANSFER_IN_OTHER,

        // Sub-categories: Transfer Out
        "WITHDRAWAL" to StringKey.CATEGORY_TRANSFER_OUT_WITHDRAWAL,
        "OTHER_TRANSFER_OUT" to StringKey.CATEGORY_TRANSFER_OUT_OTHER,

        // Sub-categories: Loan Payments
        "CAR_PAYMENT" to StringKey.CATEGORY_LOAN_CAR,
        "CREDIT_CARD_PAYMENT" to StringKey.CATEGORY_LOAN_CREDIT_CARD,
        "PERSONAL_LOAN_PAYMENT" to StringKey.CATEGORY_LOAN_PERSONAL,
        "MORTGAGE_PAYMENT" to StringKey.CATEGORY_LOAN_MORTGAGE,
        "STUDENT_LOAN" to StringKey.CATEGORY_LOAN_STUDENT,
        "STUDENT_LOAN_PAYMENT" to StringKey.CATEGORY_LOAN_STUDENT, // Plaid detailed code alias
        "OTHER_PAYMENT" to StringKey.CATEGORY_LOAN_OTHER,

        // Sub-categories: Bank Fees
        "ATM_FEES" to StringKey.CATEGORY_BANK_ATM,
        "FOREIGN_TRANSACTION_FEES" to StringKey.CATEGORY_BANK_FOREIGN_TRANSACTION,
        "INSUFFICIENT_FUNDS" to StringKey.CATEGORY_BANK_INSUFFICIENT_FUNDS,
        "INTEREST_CHARGE" to StringKey.CATEGORY_BANK_INTEREST_CHARGE,
        "OVERDRAFT_FEES" to StringKey.CATEGORY_BANK_OVERDRAFT,
        "OTHER_BANK_FEES" to StringKey.CATEGORY_BANK_OTHER,

        // Sub-categories: Entertainment
        "CASINOS_AND_GAMBLING" to StringKey.CATEGORY_ENTERTAINMENT_GAMBLING,
        "MUSIC_AND_AUDIO" to StringKey.CATEGORY_ENTERTAINMENT_MUSIC,
        "MUSIC" to StringKey.CATEGORY_ENTERTAINMENT_MUSIC, // Alias for short-form code
        "SPORTING_EVENTS_AMUSEMENT_PARKS_AND_MUSEUMS" to StringKey.CATEGORY_ENTERTAINMENT_EVENTS,
        "TV_AND_MOVIES" to StringKey.CATEGORY_ENTERTAINMENT_TV_MOVIES,
        "VIDEO_GAMES" to StringKey.CATEGORY_ENTERTAINMENT_VIDEO_GAMES,
        "OTHER_ENTERTAINMENT" to StringKey.CATEGORY_ENTERTAINMENT_OTHER,

        // Sub-categories: Food and Drink
        "BEER_WINE_AND_LIQUOR" to StringKey.CATEGORY_FOOD_ALCOHOL,
        "COFFEE" to StringKey.CATEGORY_FOOD_COFFEE,
        "FAST_FOOD" to StringKey.CATEGORY_FOOD_FAST_FOOD,
        "GROCERIES" to StringKey.CATEGORY_FOOD_GROCERIES,
        "RESTAURANT" to StringKey.CATEGORY_FOOD_RESTAURANT,
        "VENDING_MACHINES" to StringKey.CATEGORY_FOOD_VENDING,
        "OTHER_FOOD_AND_DRINK" to StringKey.CATEGORY_FOOD_OTHER,

        // Sub-categories: General Merchandise
        "BOOKSTORES_AND_NEWSSTANDS" to StringKey.CATEGORY_MERCHANDISE_BOOKS,
        "CLOTHING_AND_ACCESSORIES" to StringKey.CATEGORY_MERCHANDISE_CLOTHING,
        "CONVENIENCE_STORES" to StringKey.CATEGORY_MERCHANDISE_CONVENIENCE,
        "DEPARTMENT_STORES" to StringKey.CATEGORY_MERCHANDISE_DEPARTMENT,
        "DISCOUNT_STORES" to StringKey.CATEGORY_MERCHANDISE_DISCOUNT,
        "ELECTRONICS" to StringKey.CATEGORY_MERCHANDISE_ELECTRONICS,
        "GIFTS_AND_NOVELTIES" to StringKey.CATEGORY_MERCHANDISE_GIFTS,
        "OFFICE_SUPPLIES" to StringKey.CATEGORY_MERCHANDISE_OFFICE,
        "ONLINE_MARKETPLACES" to StringKey.CATEGORY_MERCHANDISE_ONLINE,
        "PET_SUPPLIES" to StringKey.CATEGORY_MERCHANDISE_PET,
        "SPORTING_GOODS" to StringKey.CATEGORY_MERCHANDISE_SPORTING,
        "SUPERSTORES" to StringKey.CATEGORY_MERCHANDISE_SUPERSTORES,
        "TOBACCO_AND_VAPE" to StringKey.CATEGORY_MERCHANDISE_TOBACCO,
        "OTHER_GENERAL_MERCHANDISE" to StringKey.CATEGORY_MERCHANDISE_OTHER,

        // Sub-categories: Home Improvement
        "FURNITURE" to StringKey.CATEGORY_HOME_FURNITURE,
        "HARDWARE" to StringKey.CATEGORY_HOME_HARDWARE,
        "REPAIR_AND_MAINTENANCE" to StringKey.CATEGORY_HOME_REPAIR,
        "SECURITY" to StringKey.CATEGORY_HOME_SECURITY,
        "OTHER_HOME_IMPROVEMENT" to StringKey.CATEGORY_HOME_OTHER,

        // Sub-categories: Medical
        "DENTAL_CARE" to StringKey.CATEGORY_MEDICAL_DENTAL,
        "EYE_CARE" to StringKey.CATEGORY_MEDICAL_EYE,
        "NURSING_CARE" to StringKey.CATEGORY_MEDICAL_NURSING,
        "PHARMACIES_AND_SUPPLEMENTS" to StringKey.CATEGORY_MEDICAL_PHARMACY,
        "PRIMARY_CARE" to StringKey.CATEGORY_MEDICAL_PRIMARY,
        "VETERINARY_SERVICES" to StringKey.CATEGORY_MEDICAL_VETERINARY,
        "OTHER_MEDICAL" to StringKey.CATEGORY_MEDICAL_OTHER,

        // Sub-categories: Personal Care
        "GYMS_AND_FITNESS_CENTERS" to StringKey.CATEGORY_PERSONAL_GYM,
        "HAIR_AND_BEAUTY" to StringKey.CATEGORY_PERSONAL_HAIR_BEAUTY,
        "LAUNDRY_AND_DRY_CLEANING" to StringKey.CATEGORY_PERSONAL_LAUNDRY,
        "OTHER_PERSONAL_CARE" to StringKey.CATEGORY_PERSONAL_OTHER,

        // Sub-categories: General Services
        "ACCOUNTING_AND_FINANCIAL_PLANNING" to StringKey.CATEGORY_SERVICES_ACCOUNTING,
        "AUTOMOTIVE" to StringKey.CATEGORY_SERVICES_AUTOMOTIVE,
        "CHILDCARE" to StringKey.CATEGORY_SERVICES_CHILDCARE,
        "CONSULTING_AND_LEGAL" to StringKey.CATEGORY_SERVICES_LEGAL,
        "EDUCATION" to StringKey.CATEGORY_SERVICES_EDUCATION,
        "INSURANCE" to StringKey.CATEGORY_SERVICES_INSURANCE,
        "POSTAGE_AND_SHIPPING" to StringKey.CATEGORY_SERVICES_POSTAGE,
        "STORAGE" to StringKey.CATEGORY_SERVICES_STORAGE,
        "OTHER_GENERAL_SERVICES" to StringKey.CATEGORY_SERVICES_OTHER,

        // Sub-categories: Government and Non-Profit
        "DONATIONS" to StringKey.CATEGORY_GOVERNMENT_DONATIONS,
        "GOVERNMENT_DEPARTMENTS_AND_AGENCIES" to StringKey.CATEGORY_GOVERNMENT_AGENCIES,
        "TAX_PAYMENT" to StringKey.CATEGORY_GOVERNMENT_TAX,
        "OTHER_GOVERNMENT_AND_NON_PROFIT" to StringKey.CATEGORY_GOVERNMENT_OTHER,

        // Sub-categories: Transportation
        "BIKES_AND_SCOOTERS" to StringKey.CATEGORY_TRANSPORT_BIKES,
        "GAS" to StringKey.CATEGORY_TRANSPORT_GAS,
        "GAS_STATIONS" to StringKey.CATEGORY_TRANSPORT_GAS, // Plaid detailed code alias
        "PARKING" to StringKey.CATEGORY_TRANSPORT_PARKING,
        "PUBLIC_TRANSIT" to StringKey.CATEGORY_TRANSPORT_PUBLIC,
        "TAXIS_AND_RIDE_SHARES" to StringKey.CATEGORY_TRANSPORT_RIDESHARE,
        "TOLLS" to StringKey.CATEGORY_TRANSPORT_TOLLS,
        "OTHER_TRANSPORTATION" to StringKey.CATEGORY_TRANSPORT_OTHER,

        // Sub-categories: Travel
        "FLIGHTS" to StringKey.CATEGORY_TRAVEL_AIRLINES,
        "AIRLINES_AND_AVIATION_SERVICES" to StringKey.CATEGORY_TRAVEL_AIRLINES, // Plaid detailed code alias
        "LODGING" to StringKey.CATEGORY_TRAVEL_LODGING,
        "RENTAL_CARS" to StringKey.CATEGORY_TRAVEL_RENTAL_CARS,
        "TRAVEL_AGENCIES_AND_TOUR_OPERATORS" to StringKey.CATEGORY_TRAVEL_AGENCIES, // Plaid detailed code
        "OTHER_TRAVEL" to StringKey.CATEGORY_TRAVEL_OTHER,

        // Sub-categories: Rent and Utilities
        "GAS_AND_ELECTRICITY" to StringKey.CATEGORY_UTILITIES_GAS_ELECTRIC,
        "INTERNET_AND_CABLE" to StringKey.CATEGORY_UTILITIES_INTERNET,
        "RENT" to StringKey.CATEGORY_UTILITIES_RENT,
        "SEWAGE_AND_WASTE_MANAGEMENT" to StringKey.CATEGORY_UTILITIES_SEWAGE,
        "TELEPHONE" to StringKey.CATEGORY_UTILITIES_TELEPHONE,
        "WATER" to StringKey.CATEGORY_UTILITIES_WATER,
        "OTHER_UTILITIES" to StringKey.CATEGORY_UTILITIES_OTHER,

        // Sub-categories: Family
        "CHILD_SUPPORT" to StringKey.CATEGORY_FAMILY_CHILD_SUPPORT,
        "SPOUSAL_SUPPORT" to StringKey.CATEGORY_FAMILY_SPOUSAL_SUPPORT,
        "ELDER_CARE" to StringKey.CATEGORY_FAMILY_ELDER_CARE,
        "OTHER_FAMILY_SUPPORT" to StringKey.CATEGORY_FAMILY_OTHER,
    )

    /**
     * Locale-aware version of formatCategoryName. Uses translated StringKeys when available,
     * falls back to Title Case conversion.
     */
    fun formatCategoryNameLocalized(category: String): String {
        val normalized = category.uppercase()
        val key = plaidCategoryKeys[normalized]
            ?: plaidCategoryKeys[normalized.replace(" ", "_")] // handle display-format input (e.g. "Beer wine and liquor")
            ?: return formatCategoryName(category)
        return key.tr()
    }

    /**
     * Locale-aware version of formatTwoLevelCategory.
     */
    fun formatTwoLevelCategoryLocalized(
        primary: String,
        detailed: String,
    ): String {
        val formattedPrimary = formatCategoryNameLocalized(primary)

        val subcategory = detailed.removePrefix("${primary}_")
        val formattedSubcategory =
            if (subcategory != detailed) {
                formatCategoryNameLocalized(subcategory)
            } else {
                formatCategoryNameLocalized(detailed)
            }

        // Guard: if both levels format to the same string, show only one level
        if (formattedPrimary == formattedSubcategory) return formattedPrimary
        return "$formattedPrimary → $formattedSubcategory"
    }

    /**
     * Gets the appropriate emoji icon for a category based on the category name and merchant.
     * This provides consistent iconography across all platforms.
     *
     * Strategy: Check Plaid category FIRST (more reliable), then fall back to merchant name if no category match.
     *
     * @param category The transaction category (formatted display string, e.g., "Transportation → Gas")
     * @param merchant The merchant name (optional, used as fallback when category is not matched)
     * @return Emoji string representing the category
     */
    fun getCategoryIcon(
        category: String,
        merchant: String = "",
    ): String = iconIdToEmoji(getCategoryIconId(category, merchant))

    /**
     * Returns a stable icon identifier for the given category/merchant combination.
     * Used by Web for SVG icon lookup; Android/iOS use getCategoryIcon() for emojis.
     *
     * @return Icon ID string (e.g., "car", "coffee", "kitchen_tools")
     */
    /**
     * Definitive mapping from Plaid detailed category codes to icon IDs.
     * This is the single source of truth for category → icon resolution.
     */
    private val plaidCodeToIcon = mapOf(
        // Income
        "INCOME_DIVIDENDS" to "tax",
        "INCOME_INTEREST_EARNED" to "tax",
        "INCOME_RETIREMENT_PENSION" to "tax",
        "INCOME_TAX_REFUND" to "cash",
        "INCOME_UNEMPLOYMENT" to "cash",
        "INCOME_WAGES" to "cash",
        "INCOME_OTHER_INCOME" to "cash",
        // Transfer In
        "TRANSFER_IN_CASH_ADVANCES_AND_LOANS" to "tax",
        "TRANSFER_IN_DEPOSIT" to "cash_back",
        "TRANSFER_IN_INVESTMENT_AND_RETIREMENT_FUNDS" to "bank",
        "TRANSFER_IN_SAVINGS" to "tax",
        "TRANSFER_IN_ACCOUNT_TRANSFER" to "cash_back",
        "TRANSFER_IN_OTHER_TRANSFER_IN" to "cash_back",
        // Transfer Out
        "TRANSFER_OUT_INVESTMENT_AND_RETIREMENT_FUNDS" to "bank",
        "TRANSFER_OUT_SAVINGS" to "tax",
        "TRANSFER_OUT_WITHDRAWAL" to "cash_forward",
        "TRANSFER_OUT_ACCOUNT_TRANSFER" to "repeat",
        "TRANSFER_OUT_OTHER_TRANSFER_OUT" to "cash_forward",
        // Loan Payments
        "LOAN_PAYMENTS_CAR_PAYMENT" to "car",
        "LOAN_PAYMENTS_CREDIT_CARD_PAYMENT" to "credit_card",
        "LOAN_PAYMENTS_PERSONAL_LOAN_PAYMENT" to "bank",
        "LOAN_PAYMENTS_MORTGAGE_PAYMENT" to "home",
        "LOAN_PAYMENTS_STUDENT_LOAN_PAYMENT" to "school",
        "LOAN_PAYMENTS_OTHER_PAYMENT" to "bank",
        // Bank Fees
        "BANK_FEES_ATM_FEES" to "bank",
        "BANK_FEES_FOREIGN_TRANSACTION_FEES" to "bank",
        "BANK_FEES_INSUFFICIENT_FUNDS" to "bank",
        "BANK_FEES_INTEREST_CHARGE" to "bank",
        "BANK_FEES_OVERDRAFT_FEES" to "bank",
        "BANK_FEES_OTHER_BANK_FEES" to "bank",
        // Entertainment
        "ENTERTAINMENT_CASINOS_AND_GAMBLING" to "dice",
        "ENTERTAINMENT_MUSIC_AND_AUDIO" to "music",
        "ENTERTAINMENT_SPORTING_EVENTS_AMUSEMENT_PARKS_AND_MUSEUMS" to "football",
        "ENTERTAINMENT_TV_AND_MOVIES" to "device_tv",
        "ENTERTAINMENT_VIDEO_GAMES" to "gamepad",
        "ENTERTAINMENT_OTHER_ENTERTAINMENT" to "confetti",
        // Food and Drink
        "FOOD_AND_DRINK_BEER_WINE_AND_LIQUOR" to "glass",
        "FOOD_AND_DRINK_COFFEE" to "coffee",
        "FOOD_AND_DRINK_FAST_FOOD" to "burger",
        "FOOD_AND_DRINK_GROCERIES" to "shopping_cart",
        "FOOD_AND_DRINK_RESTAURANT" to "kitchen_tools",
        "FOOD_AND_DRINK_VENDING_MACHINES" to "bubble_tea",
        "FOOD_AND_DRINK_OTHER_FOOD_AND_DRINK" to "kitchen_tools",
        // General Merchandise
        "GENERAL_MERCHANDISE_BOOKSTORES_AND_NEWSSTANDS" to "book",
        "GENERAL_MERCHANDISE_CLOTHING_AND_ACCESSORIES" to "shirt",
        "GENERAL_MERCHANDISE_CONVENIENCE_STORES" to "coin",
        "GENERAL_MERCHANDISE_DEPARTMENT_STORES" to "shirt",
        "GENERAL_MERCHANDISE_DISCOUNT_STORES" to "discount",
        "GENERAL_MERCHANDISE_ELECTRONICS" to "device_remote",
        "GENERAL_MERCHANDISE_GIFTS_AND_NOVELTIES" to "gift",
        "GENERAL_MERCHANDISE_OFFICE_SUPPLIES" to "paperclip",
        "GENERAL_MERCHANDISE_ONLINE_MARKETPLACES" to "truck",
        "GENERAL_MERCHANDISE_PET_SUPPLIES" to "cat",
        "GENERAL_MERCHANDISE_SPORTING_GOODS" to "barbell",
        "GENERAL_MERCHANDISE_SUPERSTORES" to "shopping_cart",
        "GENERAL_MERCHANDISE_TOBACCO_AND_VAPE" to "smoking",
        "GENERAL_MERCHANDISE_OTHER_GENERAL_MERCHANDISE" to "shopping_bag",
        // Home Improvement
        "HOME_IMPROVEMENT_FURNITURE" to "home",
        "HOME_IMPROVEMENT_HARDWARE" to "hammer",
        "HOME_IMPROVEMENT_REPAIR_AND_MAINTENANCE" to "hammer",
        "HOME_IMPROVEMENT_SECURITY" to "shield",
        "HOME_IMPROVEMENT_OTHER_HOME_IMPROVEMENT" to "home",
        // Medical
        "MEDICAL_DENTAL_CARE" to "dental",
        "MEDICAL_EYE_CARE" to "eye",
        "MEDICAL_NURSING_CARE" to "baby_carriage",
        "MEDICAL_PHARMACIES_AND_SUPPLEMENTS" to "prescription",
        "MEDICAL_PRIMARY_CARE" to "prescription",
        "MEDICAL_VETERINARY_SERVICES" to "cat",
        "MEDICAL_OTHER_MEDICAL" to "prescription",
        // Personal Care
        "PERSONAL_CARE_GYMS_AND_FITNESS_CENTERS" to "barbell",
        "PERSONAL_CARE_HAIR_AND_BEAUTY" to "scissors",
        "PERSONAL_CARE_LAUNDRY_AND_DRY_CLEANING" to "wash_dry",
        "PERSONAL_CARE_OTHER_PERSONAL_CARE" to "scissors",
        // General Services
        "GENERAL_SERVICES_ACCOUNTING_AND_FINANCIAL_PLANNING" to "abacus",
        "GENERAL_SERVICES_AUTOMOTIVE" to "car",
        "GENERAL_SERVICES_CHILDCARE" to "baby_carriage",
        "GENERAL_SERVICES_CONSULTING_AND_LEGAL" to "receipt",
        "GENERAL_SERVICES_EDUCATION" to "school",
        "GENERAL_SERVICES_INSURANCE" to "shield",
        "GENERAL_SERVICES_POSTAGE_AND_SHIPPING" to "truck",
        "GENERAL_SERVICES_STORAGE" to "warehouse",
        "GENERAL_SERVICES_OTHER_GENERAL_SERVICES" to "receipt",
        // Government and Non-Profit
        "GOVERNMENT_AND_NON_PROFIT_DONATIONS" to "heart_handshake",
        "GOVERNMENT_AND_NON_PROFIT_GOVERNMENT_DEPARTMENTS_AND_AGENCIES" to "receipt",
        "GOVERNMENT_AND_NON_PROFIT_TAX_PAYMENT" to "receipt",
        "GOVERNMENT_AND_NON_PROFIT_OTHER_GOVERNMENT_AND_NON_PROFIT" to "receipt",
        // Transportation
        "TRANSPORTATION_BIKES_AND_SCOOTERS" to "car",
        "TRANSPORTATION_GAS_STATIONS" to "gas_station",
        "TRANSPORTATION_PARKING" to "parking_circle",
        "TRANSPORTATION_PUBLIC_TRANSIT" to "bus",
        "TRANSPORTATION_TAXIS_AND_RIDE_SHARES" to "car",
        "TRANSPORTATION_TOLLS" to "car",
        "TRANSPORTATION_OTHER_TRANSPORTATION" to "bus",
        // Travel
        "TRAVEL_AIRLINES_AND_AVIATION_SERVICES" to "plane",
        "TRAVEL_LODGING" to "bed",
        "TRAVEL_RENTAL_CARS" to "car",
        "TRAVEL_TRAVEL_AGENCIES_AND_TOUR_OPERATORS" to "map",
        "TRAVEL_OTHER_TRAVEL" to "map",
        // Rent and Utilities
        "RENT_AND_UTILITIES_GAS_AND_ELECTRICITY" to "bolt",
        "RENT_AND_UTILITIES_INTERNET_AND_CABLE" to "wifi",
        "RENT_AND_UTILITIES_RENT" to "home",
        "RENT_AND_UTILITIES_SEWAGE_AND_WASTE_MANAGEMENT" to "trash",
        "RENT_AND_UTILITIES_TELEPHONE" to "mobile",
        "RENT_AND_UTILITIES_WATER" to "droplet",
        "RENT_AND_UTILITIES_OTHER_UTILITIES" to "bolt",
        // Family
        "FAMILY_CHILD_SUPPORT" to "mood_kid",
        "FAMILY_SPOUSAL_SUPPORT" to "user",
        "FAMILY_ELDER_CARE" to "wheelchair",
        "FAMILY_OTHER_FAMILY_SUPPORT" to "user",
    )

    /**
     * Look up icon by Plaid code directly. Returns null if not found.
     */
    fun getIconIdByPlaidCode(plaidCode: String): String? = plaidCodeToIcon[plaidCode]

    fun getCategoryIconId(
        category: String,
        merchant: String = "",
    ): String {
        // STEP 0: Direct plaidCode lookup (most reliable — used by category selectors)
        plaidCodeToIcon[category]?.let { return it }

        // Normalize underscores to spaces so Plaid codes match display name rules.
        // Also convert "/" to " → " so "PRIMARY/DETAILED" matches arrow-based rules.
        val categoryLower = category.lowercase().replace('_', ' ').replace("/", " → ")
        val merchantLower = merchant.lowercase()

        // STEP 1: Check Plaid categories FIRST (structured and reliable)
        // Icon assignments match docs/icons/icon-review-editable.html reference

        // Bank Fees
        when {
            categoryLower.contains("bank fee") || categoryLower.contains("→ atm fee") ||
                categoryLower.contains("→ overdraft") || categoryLower.contains("→ insufficient") ||
                categoryLower.contains("→ foreign transaction") || categoryLower.contains("→ interest charge") -> return "bank"
        }

        // Entertainment categories
        when {
            categoryLower.contains("→ casino") || categoryLower.contains("→ gambling") -> return "dice"
            categoryLower.contains("→ music") || categoryLower.contains("→ audio") -> return "music"
            categoryLower.contains("→ sporting event") || categoryLower.contains("→ amusement") || categoryLower.contains("→ museum") -> return "football"
            categoryLower.contains("→ tv") || categoryLower.contains("→ movie") || categoryLower.contains("→ cinema") || categoryLower.contains("→ streaming") || categoryLower.contains("→ video") -> return "device_tv"
            categoryLower.contains("→ gaming") || categoryLower.contains("→ video game") -> return "gamepad"
            categoryLower.contains("entertainment") -> return "music"
        }

        // Food and Drink categories
        when {
            categoryLower.contains("→ beer") || categoryLower.contains("→ wine") || categoryLower.contains("→ liquor") ||
                categoryLower.contains("→ bar") || categoryLower.contains("→ brewery") -> return "glass"
            categoryLower.contains("→ coffee") || categoryLower.contains("→ cafe") -> return "coffee"
            categoryLower.contains("→ fast food") || categoryLower.contains("→ burger") -> return "burger"
            categoryLower.contains("→ groceries") || categoryLower.contains("→ grocery") || categoryLower.contains("→ supermarket") -> return "shopping_cart"
            categoryLower.contains("→ restaurant") -> return "kitchen_tools"
            categoryLower.contains("→ vending") -> return "bubble_tea"
            categoryLower.contains("food and drink") || categoryLower.contains("food & drink") -> return "kitchen_tools"
        }

        // General Merchandise (Shopping)
        when {
            categoryLower.contains("→ bookstore") || categoryLower.contains("→ newsstand") -> return "book"
            categoryLower.contains("→ clothing") || categoryLower.contains("→ apparel") || categoryLower.contains("→ department store") -> return "shirt"
            categoryLower.contains("→ convenience store") -> return "coin"
            categoryLower.contains("→ discount store") -> return "discount"
            categoryLower.contains("→ electronics") -> return "device_remote"
            categoryLower.contains("→ gift") || categoryLower.contains("→ novelti") -> return "gift"
            categoryLower.contains("→ office suppli") -> return "paperclip"
            categoryLower.contains("→ online marketplace") || categoryLower.contains("→ shipping") -> return "truck"
            categoryLower.contains("→ pet suppli") -> return "cat"
            categoryLower.contains("→ sporting good") -> return "barbell"
            categoryLower.contains("→ superstore") -> return "shopping_cart"
            categoryLower.contains("→ tobacco") || categoryLower.contains("→ vape") -> return "smoking"
            categoryLower.contains("→ shoes") || categoryLower.contains("→ footwear") -> return "shopping_bag"
            categoryLower.contains("general merchandise") || categoryLower.contains("shopping") -> return "shopping_bag"
        }

        // General Services
        when {
            categoryLower.contains("→ accounting") || categoryLower.contains("→ financial planning") -> return "abacus"
            categoryLower.contains("→ automotive") -> return "car"
            categoryLower.contains("→ childcare") -> return "baby_carriage"
            categoryLower.contains("→ consulting") || categoryLower.contains("→ legal") -> return "receipt"
            categoryLower.contains("→ education") || categoryLower.contains("→ tuition") -> return "school"
            categoryLower.contains("→ insurance") -> return "shield"
            categoryLower.contains("→ postage") || categoryLower.contains("→ shipping") -> return "truck"
            categoryLower.contains("→ storage") -> return "warehouse"
            categoryLower.contains("general services") -> return "receipt"
        }

        // Government and Non-Profit
        when {
            categoryLower.contains("→ donation") || categoryLower.contains("→ charity") -> return "heart_handshake"
            categoryLower.contains("government") || categoryLower.contains("→ tax payment") || categoryLower.contains("→ tax refund") -> return "receipt"
        }

        // Home Improvement
        when {
            categoryLower.contains("→ furniture") -> return "home"
            categoryLower.contains("→ hardware") || categoryLower.contains("→ repair") || categoryLower.contains("→ maintenance") -> return "hammer"
            categoryLower.contains("→ security") -> return "shield"
            categoryLower.contains("home improvement") -> return "home"
        }

        // Transportation categories
        when {
            (categoryLower.contains("→ gas") && !categoryLower.contains("electricity")) || categoryLower.contains("transportation gas") -> return "gas_station"
            categoryLower.contains("→ parking") || categoryLower.contains("transportation parking") -> return "parking_circle"
            categoryLower.contains("→ taxi") || categoryLower.contains("ride share") || categoryLower.contains("rideshare") -> return "car"
            categoryLower.contains("→ public") || categoryLower.contains("→ transit") -> return "bus"
            categoryLower.contains("→ subway") || categoryLower.contains("→ metro") -> return "bus"
            categoryLower.contains("→ bus") -> return "bus"
            categoryLower.contains("→ airline") || categoryLower.contains("→ flight") -> return "plane"
            categoryLower.contains("→ bicycle") || categoryLower.contains("→ bike") || categoryLower.contains("→ scooter") -> return "car"
            categoryLower.contains("→ rental") && categoryLower.contains("car") -> return "car"
            categoryLower.contains("→ toll") -> return "car"
            categoryLower.contains("transportation") -> return "bus"
        }

        // Medical / Healthcare categories
        when {
            categoryLower.contains("→ dental") || categoryLower.contains("→ dentist") -> return "dental"
            categoryLower.contains("→ eye") || categoryLower.contains("→ optician") || categoryLower.contains("→ vision") -> return "eye"
            categoryLower.contains("→ nursing") -> return "baby_carriage"
            categoryLower.contains("→ pharmac") || categoryLower.contains("→ supplement") || categoryLower.contains("→ drugstore") -> return "prescription"
            categoryLower.contains("→ veterinar") -> return "cat"
            categoryLower.contains("medical") || categoryLower.contains("healthcare") || categoryLower.contains("health") -> return "prescription"
        }

        // Personal Care
        when {
            categoryLower.contains("→ gym") || categoryLower.contains("→ fitness") -> return "barbell"
            categoryLower.contains("→ hair") || categoryLower.contains("→ beauty") -> return "scissors"
            categoryLower.contains("→ laundry") || categoryLower.contains("→ dry clean") -> return "wash_dry"
            categoryLower.contains("→ yoga") || categoryLower.contains("→ pilates") -> return "barbell"
            categoryLower.contains("personal care") -> return "scissors"
        }

        // Rent and Utilities
        when {
            categoryLower.contains("gas and electricity") -> return "bolt"
            categoryLower.contains("→ electric") || categoryLower.contains("electricity") -> return "bolt"
            categoryLower.contains("→ water") || categoryLower.contains("water bill") -> return "droplet"
            categoryLower.contains("→ sewage") || categoryLower.contains("→ waste") -> return "trash"
            categoryLower.contains("→ gas") && categoryLower.contains("utilities") -> return "bolt"
            categoryLower.contains("→ internet") || categoryLower.contains("→ cable") -> return "wifi"
            categoryLower.contains("→ phone") || categoryLower.contains("→ mobile") || categoryLower.contains("→ wireless") || categoryLower.contains("→ telephone") -> return "mobile"
            categoryLower.contains("→ rent") -> return "home"
            categoryLower.contains("rent and utilities") || categoryLower.contains("→ utilities") -> return "bolt"
        }

        // Travel and Lodging
        when {
            categoryLower.contains("→ hotel") || categoryLower.contains("→ lodging") -> return "bed"
            categoryLower.contains("→ vacation rental") || categoryLower.contains("→ airbnb") -> return "home"
            categoryLower.contains("→ rental car") -> return "car"
            categoryLower.contains("→ travel agenc") || categoryLower.contains("→ tour") -> return "map"
            categoryLower.contains("→ airline") || categoryLower.contains("→ flight") || categoryLower.contains("→ aviation") -> return "plane"
            categoryLower.contains("travel") -> return "map"
        }

        // Income categories
        when {
            categoryLower.contains("→ dividend") || categoryLower.contains("→ interest earned") || categoryLower.contains("→ retirement") || categoryLower.contains("→ pension") -> return "tax"
            categoryLower.contains("→ tax refund") -> return "cash"
            categoryLower.contains("→ wage") || categoryLower.contains("→ salary") || categoryLower.contains("→ payroll") -> return "cash"
            categoryLower.contains("income") -> return "cash"
        }

        // Transfer In
        when {
            categoryLower.contains("transfer in") && categoryLower.contains("→ investment") -> return "bank"
            categoryLower.contains("transfer in") && categoryLower.contains("→ saving") -> return "tax"
            categoryLower.contains("transfer in") && categoryLower.contains("→ cash advance") -> return "tax"
            categoryLower.contains("transfer in") && (categoryLower.contains("→ deposit") || categoryLower.contains("→ account transfer")) -> return "cash_back"
            categoryLower.contains("transfer in") -> return "cash_back"
        }

        // Transfer Out
        when {
            categoryLower.contains("transfer out") && categoryLower.contains("→ investment") -> return "bank"
            categoryLower.contains("transfer out") && categoryLower.contains("→ saving") -> return "tax"
            categoryLower.contains("transfer out") && categoryLower.contains("→ withdrawal") -> return "cash_forward"
            categoryLower.contains("transfer out") && categoryLower.contains("→ account transfer") -> return "repeat"
            categoryLower.contains("transfer out") -> return "cash_forward"
        }

        // Loan Payments
        when {
            categoryLower.contains("→ car payment") -> return "car"
            categoryLower.contains("→ credit card payment") -> return "credit_card"
            categoryLower.contains("→ mortgage payment") -> return "home"
            categoryLower.contains("→ student loan") -> return "school"
            categoryLower.contains("loan") -> return "bank"
        }

        // Housing (generic, after Rent and Utilities)
        when {
            categoryLower.contains("rent") || categoryLower.contains("mortgage") ||
                categoryLower.contains("housing") -> return "home"
        }

        // Family
        when {
            categoryLower.contains("child support") || categoryLower.contains("child_support") -> return "mood_kid"
            categoryLower.contains("elder care") || categoryLower.contains("elder_care") -> return "wheelchair"
            categoryLower.contains("spousal support") || categoryLower.contains("spousal_support") -> return "user"
            categoryLower.contains("family") -> return "user"
        }

        // STEP 2: No category match found - fall back to merchant name patterns

        // Food merchant fallbacks
        when {
            merchantLower.contains("starbucks") || merchantLower.contains("coffee") || merchantLower.contains("bean") -> return "coffee"
            merchantLower.contains("mcdonald") || merchantLower.contains("burger king") || merchantLower.contains("wendy") -> return "kitchen_tools"
            merchantLower.contains("pizza") -> return "kitchen_tools"
            merchantLower.contains("restaurant") || merchantLower.contains("bistro") || merchantLower.contains("grill") -> return "kitchen_tools"
            merchantLower.contains("pub") || merchantLower.contains("brewery") -> return "glass"
            merchantLower.contains("bakery") || merchantLower.contains("donut") -> return "kitchen_tools"
            merchantLower.contains("sushi") || merchantLower.contains("japanese") -> return "kitchen_tools"
            merchantLower.contains("mexican") || merchantLower.contains("taco") -> return "kitchen_tools"
            merchantLower.contains("chinese") || merchantLower.contains("asian") -> return "kitchen_tools"
        }

        // Transportation merchant fallbacks
        when {
            merchantLower.contains("uber") || merchantLower.contains("lyft") -> return "car"
            merchantLower.contains("shell") ||
                merchantLower.contains("exxon") ||
                merchantLower.contains("chevron") ||
                merchantLower.contains("mobil") ||
                merchantLower.contains("76") ||
                merchantLower.contains("arco") ||
                merchantLower.contains("bp") ||
                merchantLower.contains("citgo") -> return "gas_station"
            merchantLower.contains("parking") -> return "car"
            merchantLower.contains("amtrak") || merchantLower.contains("rail") -> return "bus"
            merchantLower.contains("united airlines") ||
                merchantLower.contains("delta") ||
                merchantLower.contains("american airlines") -> return "plane"
            merchantLower.contains("hertz") || merchantLower.contains("enterprise") || merchantLower.contains("rental car") -> return "car"
        }

        // Shopping merchant fallbacks
        when {
            merchantLower.contains("whole foods") ||
                merchantLower.contains("trader joe") ||
                merchantLower.contains("safeway") ||
                merchantLower.contains("kroger") ||
                merchantLower.contains("publix") -> return "shopping_cart"
            merchantLower.contains("amazon") -> return "truck"
            merchantLower.contains("target") -> return "shopping_bag"
            merchantLower.contains("walmart") -> return "shopping_bag"
            merchantLower.contains("apple store") -> return "shopping_bag"
            merchantLower.contains("best buy") -> return "device_tv"
            merchantLower.contains("home depot") || merchantLower.contains("lowe") -> return "hammer"
            merchantLower.contains("cvs") || merchantLower.contains("walgreens") || merchantLower.contains("rite aid") -> return "first_aid"
        }

        // Entertainment merchant fallbacks
        when {
            merchantLower.contains("netflix") ||
                merchantLower.contains("hulu") ||
                merchantLower.contains("disney") ||
                merchantLower.contains("hbo") ||
                merchantLower.contains("paramount") -> return "device_tv"
            merchantLower.contains("spotify") || merchantLower.contains("apple music") || merchantLower.contains("pandora") -> return "music"
            merchantLower.contains("amc") || merchantLower.contains("cinema") || merchantLower.contains("theater") -> return "device_tv"
            merchantLower.contains("playstation") ||
                merchantLower.contains("xbox") ||
                merchantLower.contains("steam") ||
                merchantLower.contains("nintendo") -> return "gamepad"
        }

        // Health merchant fallbacks
        when {
            merchantLower.contains("hospital") || merchantLower.contains("emergency") -> return "first_aid"
            merchantLower.contains("gym") || merchantLower.contains("fitness") || merchantLower.contains("planet fitness") -> return "barbell"
        }

        // Services merchant fallbacks
        when {
            merchantLower.contains("verizon") || merchantLower.contains("t-mobile") || merchantLower.contains("at&t") -> return "mobile"
            merchantLower.contains("comcast") || merchantLower.contains("spectrum") -> return "wifi"
        }

        // Lodging merchant fallbacks
        when {
            merchantLower.contains("marriott") || merchantLower.contains("hilton") || merchantLower.contains("hyatt") -> return "bed"
            merchantLower.contains("airbnb") || merchantLower.contains("vrbo") -> return "home"
        }

        // Financial merchant fallbacks
        when {
            merchantLower.contains("zelle") || merchantLower.contains("venmo") || merchantLower.contains("paypal") -> return "repeat"
        }

        // Default fallback icon
        return "shopping_bag"
    }

    private fun iconIdToEmoji(iconId: String): String = when (iconId) {
        "car" -> "🚗"
        "gas_station" -> "⛽"
        "bus" -> "🚌"
        "plane" -> "✈️"
        "coffee" -> "☕"
        "kitchen_tools" -> "🍽️"
        "glass" -> "🍺"
        "shopping_bag" -> "🛍️"
        "shopping_cart" -> "🛒"
        "shirt" -> "👕"
        "device_tv" -> "📺"
        "gamepad" -> "🎮"
        "first_aid" -> "🏥"
        "barbell" -> "💪"
        "scissors" -> "✂️"
        "home" -> "🏠"
        "bolt" -> "⚡"
        "mobile" -> "📱"
        "wifi" -> "🌐"
        "bank" -> "🏦"
        "cash" -> "💰"
        "credit_card" -> "💳"
        "repeat" -> "🔄"
        "receipt" -> "📝"
        "bed" -> "🏨"
        "shield" -> "🛡️"
        "school" -> "🎓"
        "hammer" -> "🔨"
        "heart_handshake" -> "❤️"
        "truck" -> "📦"
        "droplet" -> "💧"
        else -> "🛍️"
    }

    /**
     * Gets the appropriate color hex code for a category.
     * This provides consistent colors across all platforms (iOS, Android, Web).
     *
     * @param category The transaction category string
     * @return Hex color string (e.g., "#FF9800")
     */
    fun getCategoryColor(category: String, userOverrides: Map<String, String>? = null): String {
        // Check user override first
        if (userOverrides != null) {
            val normalizedLower = category.lowercase().replace(' ', '_')
            val normalizedUpper = category.uppercase().replace(' ', '_')
            userOverrides[normalizedLower]?.let { return it }
            userOverrides[normalizedUpper]?.let { return it }
            userOverrides[category]?.let { return it }
            // Also try matching just the primary part (before " → " or " / ")
            val primaryPart = category.split(" → ", " / ", "/").first().trim()
            if (primaryPart != category) {
                val primaryLower = primaryPart.lowercase().replace(' ', '_')
                val primaryUpper = primaryPart.uppercase().replace(' ', '_')
                userOverrides[primaryLower]?.let { return it }
                userOverrides[primaryUpper]?.let { return it }
                userOverrides[primaryPart]?.let { return it }
            }
        }

        // Normalize underscores to spaces so Plaid codes ("TRANSFER_OUT") match
        // the same rules as display names ("Transfer Out").
        val categoryLower = category.lowercase().replace('_', ' ')

        // Match primary category FIRST (before substring matches hit false positives).
        // Colors match docs/icons/icon-review-editable.html reference.
        return when {
            categoryLower.contains("bank fee") -> "#795548"                  // Brown
            categoryLower.contains("entertainment") -> "#F44336"             // Red
            categoryLower.contains("food and drink") || categoryLower.contains("food & drink") -> "#FF9800" // Orange
            categoryLower.contains("general merchandise") -> "#9C27B0"       // Purple
            // Insurance is a child of GENERAL_SERVICES but the app promotes it to its own
            // line — check it FIRST so "general services insurance" resolves here, and give
            // it a lighter shade of the parent's violet: distinct, yet visibly related.
            categoryLower.contains("insurance") -> "#B39DDB"                  // Light violet
            categoryLower.contains("general services") -> "#7E57C2"          // Violet (distinct from Rent & Utilities' blue-grey)
            categoryLower.contains("government") || categoryLower.contains("non-profit") -> "#C0CA33" // Lime (distinct from Personal Care's pink)
            categoryLower.contains("home improvement") -> "#A1887F"          // Taupe (distinct from Bank Fees' dark brown)
            categoryLower.contains("loan payment") -> "#009688"              // Teal
            categoryLower.contains("medical") || categoryLower.contains("healthcare") || categoryLower.contains("health") -> "#00BCD4" // Cyan
            categoryLower.contains("personal care") -> "#E91E63"             // Pink
            categoryLower.contains("rent and utilities") || categoryLower.contains("rent & utilities") -> "#607D8B" // Blue Grey
            categoryLower.contains("transportation") -> "#2196F3"            // Blue
            categoryLower.contains("travel") -> "#3F51B5"                    // Indigo
            categoryLower.contains("income") -> "#4CAF50"                    // Green
            categoryLower.contains("transfer in") -> "#FDD835"               // Yellow (distinct from Loan Payments' teal)
            categoryLower.contains("transfer out") -> "#DAA520"              // Goldenrod
            categoryLower.contains("family") -> "#FF7043"                    // Deep Orange light

            // Subcategory / keyword fallbacks (for partial strings without primary prefix)
            categoryLower.contains("restaurant") || categoryLower.contains("dining") ||
                categoryLower.contains("food") || categoryLower.contains("bistro") ||
                categoryLower.contains("coffee") || categoryLower.contains("cafe") -> "#FF9800"

            categoryLower.contains("grocery") || categoryLower.contains("supermarket") -> "#4CAF50"

            categoryLower.contains("shopping") || categoryLower.contains("clothing") ||
                categoryLower.contains("merchandise") -> "#9C27B0"

            categoryLower.contains("utilities") || categoryLower.contains("electric") ||
                categoryLower.contains("water") || categoryLower.contains("internet") ||
                categoryLower.contains("phone") || categoryLower.contains("cable") -> "#607D8B"

            categoryLower.contains("gas") && categoryLower.contains("station") -> "#2196F3"
            categoryLower.contains("gas") || categoryLower.contains("fuel") -> "#607D8B" // Utilities gas, not gas station

            categoryLower.contains("education") || categoryLower.contains("school") -> "#3F51B5"
            categoryLower.contains("hotel") || categoryLower.contains("lodging") || categoryLower.contains("airbnb") -> "#3F51B5"

            categoryLower.contains("bank") || categoryLower.contains("atm") -> "#795548"
            categoryLower.contains("transfer") || categoryLower.contains("payment") -> "#009688"
            categoryLower.contains("salary") || categoryLower.contains("payroll") || categoryLower.contains("deposit") -> "#4CAF50"

            categoryLower.contains("gym") || categoryLower.contains("fitness") -> "#E91E63"
            categoryLower.contains("pet") || categoryLower.contains("vet") -> "#00BCD4"
            categoryLower.contains("charity") || categoryLower.contains("donation") -> "#C0CA33" // Lime (Government & Non-profit)
            categoryLower.contains("rent") || categoryLower.contains("mortgage") || categoryLower.contains("housing") -> "#607D8B"

            else -> "#757575" // Grey
        }
    }

    /**
     * Gets the category color with alpha transparency.
     * Useful for backgrounds and overlays.
     *
     * @param category The transaction category string
     * @param alpha Alpha value from 0.0 (transparent) to 1.0 (opaque)
     * @return RGBA color string (e.g., "rgba(255, 152, 0, 0.5)")
     */
    fun getCategoryColorWithAlpha(category: String, alpha: Double, userOverrides: Map<String, String>? = null): String {
        val hex = getCategoryColor(category, userOverrides)
        // Convert hex to RGB components
        val r = hex.substring(1, 3).toInt(16)
        val g = hex.substring(3, 5).toInt(16)
        val b = hex.substring(5, 7).toInt(16)
        return "rgba($r, $g, $b, $alpha)"
    }

    /**
     * Data class representing confidence display information
     * @param label The text label to display (e.g., "AI: 95%", "User: 100%")
     * @param percentage The numeric confidence percentage
     * @param colorCode Color identifier: "dark_green", "pale_green", "dark_orange", "pale_red", "dark_red", "blue"
     * @param showCheckmark Whether to show a green checkmark (verified or user-changed categories)
     */
    data class ConfidenceInfo(
        val label: String,
        val percentage: Int,
        val colorCode: String,
        val showCheckmark: Boolean = false,
    )

    /**
     * Gets confidence display information based on category source and Plaid's confidence level.
     * Maps Plaid confidence levels to user-friendly percentages with colors:
     * - VERY_HIGH -> AI: 95% (dark green)
     * - HIGH -> AI: 80% (pale green)
     * - MEDIUM -> AI: 70% (dark orange)
     * - LOW -> AI: 60% (pale red)
     * - UNKNOWN -> 50% (dark red) - no "AI" prefix
     * - user -> User: 100% (blue)
     *
     * When isVerified is true or categorySource is "user", showCheckmark will be true.
     *
     * @param categorySource The source of the categorization ("plaid", "user", "ai", etc.)
     * @param confidenceLevel Plaid's confidence level string ("VERY_HIGH", "HIGH", "MEDIUM", "LOW", "UNKNOWN")
     * @param isVerified Whether the user has verified this category
     * @return ConfidenceInfo with display label, percentage, color code, and showCheckmark
     */
    fun getConfidenceInfo(
        categorySource: String,
        confidenceLevel: String?,
        isVerified: Boolean = false,
    ): ConfidenceInfo {
        // Show checkmark if verified OR user-changed (split/distributed items are NOT auto-verified)
        val showCheckmark = isVerified || categorySource.equals("user", ignoreCase = true)

        // User overrides: checkmark only, no label (user confirmed their own choice)
        if (categorySource.equals("user", ignoreCase = true)) {
            return ConfidenceInfo(
                label = "",
                percentage = 100,
                colorCode = "blue",
                showCheckmark = true,
            )
        }

        // Split/distributed children: show AI: 80% until explicitly verified by the user
        if (categorySource.equals("split", ignoreCase = true)) {
            if (isVerified) {
                // Verified split: checkmark + "AI" (no percentage)
                return ConfidenceInfo(
                    label = "AI",
                    percentage = 100,
                    colorCode = "dark_green",
                    showCheckmark = true,
                )
            }
            return ConfidenceInfo(
                label = StringKey.CONFIDENCE_AI.tr("80"),
                percentage = 80,
                colorCode = "pale_green",
                showCheckmark = false,
            )
        }

        // If verified (but not user source): checkmark + "AI" (no percentage)
        if (isVerified) {
            return ConfidenceInfo(
                label = "AI",
                percentage = 100,
                colorCode = "dark_green",
                showCheckmark = true,
            )
        }

        // Map Plaid confidence levels to percentages and colors
        return when (confidenceLevel?.uppercase()) {
            "VERY_HIGH" ->
                ConfidenceInfo(
                    label = StringKey.CONFIDENCE_AI.tr("95"),
                    percentage = 95,
                    colorCode = "dark_green",
                    showCheckmark = showCheckmark,
                )
            "HIGH" ->
                ConfidenceInfo(
                    label = StringKey.CONFIDENCE_AI.tr("80"),
                    percentage = 80,
                    colorCode = "pale_green",
                    showCheckmark = showCheckmark,
                )
            "MEDIUM" ->
                ConfidenceInfo(
                    label = StringKey.CONFIDENCE_AI.tr("70"),
                    percentage = 70,
                    colorCode = "dark_orange",
                    showCheckmark = showCheckmark,
                )
            "LOW" ->
                ConfidenceInfo(
                    label = StringKey.CONFIDENCE_AI.tr("60"),
                    percentage = 60,
                    colorCode = "pale_red",
                    showCheckmark = showCheckmark,
                )
            "UNKNOWN", null ->
                ConfidenceInfo(
                    label = "50%",
                    percentage = 50,
                    colorCode = "dark_red",
                    showCheckmark = showCheckmark,
                )
            else ->
                ConfidenceInfo(
                    label = "50%",
                    percentage = 50,
                    colorCode = "dark_red",
                    showCheckmark = showCheckmark,
                )
        }
    }

    /**
     * Maps a Plaid primary category code to a simplified display label.
     * Uses "DISPLAY" StringKeys which may differ from the raw category names.
     * For example, GENERAL_MERCHANDISE → "Shopping" (not "General Merchandise").
     */
    private fun getCategoryDisplayLabel(category: String): String {
        // Normalize: replace spaces with underscores so legacy Plaid categories
        // (e.g. "General Services") match our SCREAMING_SNAKE_CASE keys
        val normalized = category.uppercase().replace(" ", "_")
        return when (normalized) {
            "FOOD_AND_DRINK" -> StringKey.CATEGORY_FOOD_AND_DRINK.tr()
            "TRANSPORTATION" -> StringKey.CATEGORY_TRANSPORTATION.tr()
            "GENERAL_MERCHANDISE" -> StringKey.CATEGORY_DISPLAY_SHOPPING.tr()
            "ENTERTAINMENT" -> StringKey.CATEGORY_ENTERTAINMENT.tr()
            "RENT_AND_UTILITIES" -> StringKey.CATEGORY_RENT_AND_UTILITIES.tr()
            "MEDICAL" -> StringKey.CATEGORY_DISPLAY_HEALTHCARE.tr()
            "PERSONAL_CARE" -> StringKey.CATEGORY_PERSONAL_CARE.tr()
            "TRAVEL" -> StringKey.CATEGORY_TRAVEL.tr()
            "GENERAL_SERVICES" -> StringKey.CATEGORY_DISPLAY_SERVICES.tr()
            "BANK_FEES" -> StringKey.CATEGORY_BANK_FEES.tr()
            "TRANSFER_OUT" -> StringKey.CATEGORY_DISPLAY_TRANSFERS_OUT.tr()
            "TRANSFER_IN" -> StringKey.CATEGORY_DISPLAY_TRANSFERS_IN.tr()
            "LOAN_PAYMENTS" -> StringKey.CATEGORY_LOAN_PAYMENTS.tr()
            "INCOME" -> StringKey.CATEGORY_INCOME.tr()
            "HOME_IMPROVEMENT" -> StringKey.CATEGORY_DISPLAY_HOME.tr()
            "GOVERNMENT_AND_NON_PROFIT" -> StringKey.CATEGORY_DISPLAY_GOVERNMENT.tr()
            else -> category.replace("_", " ").lowercase().replaceFirstChar { it.uppercase() }
        }
    }

    /**
     * Returns user-friendly display names that match the Dashboard bar chart grouping.
     * Splits RENT_AND_UTILITIES into separate categories:
     * - "Rent/Mortgage": Rent and mortgage payments
     * - "Utilities": Gas, electricity, internet, phone, water, etc.
     * - "Subscriptions": Streaming services (from ENTERTAINMENT)
     * - "Insurance": Insurance from GENERAL_SERVICES
     *
     * Use this function everywhere category names are grouped for display
     * to ensure consistency across Activity sidebar, Dashboard charts, etc.
     */
    fun getEffectiveDisplayCategory(primaryCategory: String, detailedCategory: String): String {
        // Normalize: replace spaces with underscores so legacy Plaid categories match
        val primary = primaryCategory.uppercase().replace(" ", "_")
        val detailed = detailedCategory.uppercase().replace(" ", "_")

        return when {
            // Split RENT_AND_UTILITIES into Rent/Mortgage vs Utilities
            primary == "RENT_AND_UTILITIES" -> when {
                detailed == "RENT_AND_UTILITIES_RENT" ||
                detailed.endsWith("_RENT") ||
                (detailed.contains("RENT") && detailed != "RENT_AND_UTILITIES" && !detailed.contains("UTILITIES")) ->
                    StringKey.CATEGORY_DISPLAY_RENT_MORTGAGE.tr()
                detailed.contains("GAS_AND_ELECTRICITY") ||
                detailed.contains("INTERNET") ||
                detailed.contains("CABLE") ||
                detailed.contains("TELEPHONE") ||
                detailed.contains("WATER") ||
                detailed.contains("SEWAGE") ||
                detailed.contains("OTHER_UTILITIES") ->
                    StringKey.CATEGORY_DISPLAY_UTILITIES.tr()
                else -> StringKey.CATEGORY_DISPLAY_UTILITIES.tr()
            }
            // Mortgage payments in LOAN_PAYMENTS -> Rent/Mortgage
            primary == "LOAN_PAYMENTS" && detailed.contains("MORTGAGE") ->
                StringKey.CATEGORY_DISPLAY_RENT_MORTGAGE.tr()
            // Insurance in GENERAL_SERVICES -> Insurance
            primary == "GENERAL_SERVICES" && detailed.contains("INSURANCE") ->
                StringKey.CATEGORY_DISPLAY_INSURANCE.tr()
            // All other categories
            else -> getCategoryDisplayLabel(primary)
        }
    }

    /**
     * Checks if a confidence level requires user attention (anything below HIGH)
     * This includes MEDIUM, LOW, and UNKNOWN confidence levels
     */
    fun needsAttention(confidenceLevel: String?): Boolean =
        when (confidenceLevel?.uppercase()) {
            "VERY_HIGH", "HIGH" -> false
            else -> true
        }

    /**
     * Checks if a transaction has been manually categorized by the user
     */
    fun isUserCategorized(categorySource: String): Boolean = categorySource.equals("user", ignoreCase = true)

    /**
     * Translate an English display category name (from Transaction.effectiveDisplayCategory)
     * to the current locale. Used in dashboard charts where categories are aggregated by
     * English key first, then localized at the display layer.
     *
     * Falls back to the input string if no mapping found.
     */
    /**
     * Map a budget plan's categoryName to a display category.
     * Budget plans store either:
     *   - Primary Plaid codes: "FOOD_AND_DRINK", "RENT_AND_UTILITIES"
     *   - Detailed Plaid codes: "RENT_AND_UTILITIES_RENT", "FOOD_AND_DRINK_GROCERIES"
     *   - CUSTOM: prefixed names: "CUSTOM:My Category"
     *
     * This extracts the primary code from detailed codes and delegates to
     * getEffectiveDisplayCategory(primary, detailed) so budget keys match
     * transaction keys in spending charts.
     */
    fun mapBudgetCategoryToDisplayCategory(budgetCategory: String): String {
        if (budgetCategory.startsWith("CUSTOM:", ignoreCase = true)) {
            return budgetCategory.removePrefix("CUSTOM:").removePrefix("custom:")
        }

        val upper = budgetCategory.uppercase()

        // A parent-level RENT_AND_UTILITIES budget covers the split "Rent/Mortgage"+"Utilities"
        // display rows; attribute it to Rent/Mortgage (the dominant housing line) so rent spending
        // compares against it. Detailed RENT_AND_UTILITIES_* budgets still delegate below.
        if (upper == "RENT_AND_UTILITIES") return StringKey.CATEGORY_DISPLAY_RENT_MORTGAGE.tr()

        // Known primary category codes (order matters: longer prefixes checked first)
        val knownPrimaries = listOf(
            "GOVERNMENT_AND_NON_PROFIT",
            "GENERAL_MERCHANDISE",
            "RENT_AND_UTILITIES",
            "HOME_IMPROVEMENT",
            "GENERAL_SERVICES",
            "FOOD_AND_DRINK",
            "TRANSPORTATION",
            "PERSONAL_CARE",
            "LOAN_PAYMENTS",
            "ENTERTAINMENT",
            "TRANSFER_OUT",
            "TRANSFER_IN",
            "BANK_FEES",
            "MEDICAL",
            "TRAVEL",
            "INCOME"
        )

        // Find the primary code this budget category belongs to
        for (primary in knownPrimaries) {
            if (upper == primary || upper.startsWith("${primary}_")) {
                // Pass primary + full code as detailed, so getEffectiveDisplayCategory
                // can split RENT_AND_UTILITIES_RENT vs RENT_AND_UTILITIES_GAS_AND_ELECTRICITY etc.
                return getEffectiveDisplayCategory(primary, upper)
            }
        }

        // Fallback: try as-is (handles legacy friendly names)
        return getCategoryDisplayLabel(upper)
    }

    fun localizeDisplayCategory(english: String): String = when (english) {
        "Food & Drink"   -> StringKey.CATEGORY_FOOD_AND_DRINK.tr()
        "Transportation" -> StringKey.CATEGORY_TRANSPORTATION.tr()
        "Shopping"       -> StringKey.CATEGORY_DISPLAY_SHOPPING.tr()
        "Entertainment"  -> StringKey.CATEGORY_ENTERTAINMENT.tr()
        "Rent/Mortgage"  -> StringKey.CATEGORY_DISPLAY_RENT_MORTGAGE.tr()
        "Utilities"      -> StringKey.CATEGORY_DISPLAY_UTILITIES.tr()
        "Subscriptions"  -> StringKey.CATEGORY_DISPLAY_SUBSCRIPTIONS.tr()
        "Insurance"      -> StringKey.CATEGORY_DISPLAY_INSURANCE.tr()
        "Healthcare"     -> StringKey.CATEGORY_DISPLAY_HEALTHCARE.tr()
        "Personal Care"  -> StringKey.CATEGORY_PERSONAL_CARE.tr()
        "Travel"         -> StringKey.CATEGORY_TRAVEL.tr()
        "Services"       -> StringKey.CATEGORY_DISPLAY_SERVICES.tr()
        "Bank Fees"      -> StringKey.CATEGORY_BANK_FEES.tr()
        "Transfers Out"  -> StringKey.CATEGORY_DISPLAY_TRANSFERS_OUT.tr()
        "Transfers In"   -> StringKey.CATEGORY_DISPLAY_TRANSFERS_IN.tr()
        "Loan Payments"  -> StringKey.CATEGORY_LOAN_PAYMENTS.tr()
        "Income"         -> StringKey.CATEGORY_INCOME.tr()
        "Home"           -> StringKey.CATEGORY_DISPLAY_HOME.tr()
        "Government"     -> StringKey.CATEGORY_DISPLAY_GOVERNMENT.tr()
        else             -> english
    }
}
